import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { User, Lock, Save, Eye, EyeOff, ShieldCheck, Mail } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '../lib/api'
import { useAuthStore } from '../lib/auth'
import { handleApiError } from '../utils/errorHandler'
import LoadingButton from '../components/LoadingButton'
import { validatePhoneString } from '../lib/phoneValidation'

export default function ProfilePage() {
  const navigate = useNavigate()
  const { logout } = useAuthStore()

  const [profileLoading, setProfileLoading] = useState(true)
  const [saveProfileState, setSaveProfileState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [changePasswordState, setChangePasswordState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')

  // Baseline Initial Data for change detection
  const [initialData, setInitialData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
  })

  // Profile Form State
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'ADMIN',
  })

  // Profile Inline Validation Errors
  const [profileErrors, setProfileErrors] = useState<{
    firstName?: string
    phone?: string
  }>({})

  // Change Password Form State
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })

  // Password Inline Validation Errors
  const [passwordErrors, setPasswordErrors] = useState<{
    currentPassword?: string
    newPassword?: string
    confirmPassword?: string
  }>({})

  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  useEffect(() => {
    fetchProfile()
  }, [])

  const fetchProfile = async () => {
    try {
      setProfileLoading(true)
      const res = await api.getAdminProfile()
      const data = res.data?.data || res.data
      if (data) {
        const loadedProfile = {
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          email: data.email || '',
          phone: data.phone || '',
          role: data.role || 'ADMIN',
        }
        setProfileData(loadedProfile)
        setInitialData({
          firstName: loadedProfile.firstName,
          lastName: loadedProfile.lastName,
          phone: loadedProfile.phone,
        })
      }
    } catch (error) {
      handleApiError(error, 'Failed to load profile')
    } finally {
      setProfileLoading(false)
    }
  }

  // Profile Form Field Validation
  const validateProfileForm = (): boolean => {
    const errors: { firstName?: string; phone?: string } = {}
    const trimmedFirstName = profileData.firstName.trim()

    if (!trimmedFirstName) {
      errors.firstName = 'First name is required.'
    }

    const phoneError = validatePhoneString(profileData.phone, false)
    if (phoneError) {
      errors.phone = phoneError
    }

    setProfileErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault()

    // 1. Validate Form Fields
    if (!validateProfileForm()) {
      toast.error('Please fix the validation errors in the profile form.')
      return
    }

    const trimmedFirstName = profileData.firstName.trim()
    const trimmedLastName = profileData.lastName.trim()
    const trimmedPhone = profileData.phone.trim()

    // 2. Change Detection
    const hasChanges =
      trimmedFirstName !== initialData.firstName.trim() ||
      trimmedLastName !== initialData.lastName.trim() ||
      trimmedPhone !== initialData.phone.trim()

    if (!hasChanges) {
      toast('No changes detected.', { icon: 'ℹ️' })
      return
    }

    try {
      setSaveProfileState('loading')
      await api.updateAdminProfile({
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
        phone: trimmedPhone,
      })

      setInitialData({
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
        phone: trimmedPhone,
      })

      setSaveProfileState('success')
      toast.success('Profile updated successfully')
      setTimeout(() => setSaveProfileState('idle'), 1500)
    } catch (error) {
      setSaveProfileState('error')
      handleApiError(error, 'Failed to update profile')
      setTimeout(() => setSaveProfileState('idle'), 2000)
    }
  }

  // Change Password Form Field Validation
  const validatePasswordForm = (): boolean => {
    const errors: { currentPassword?: string; newPassword?: string; confirmPassword?: string } = {}

    const currentPass = passwordData.currentPassword.trim()
    const newPass = passwordData.newPassword
    const confirmPass = passwordData.confirmPassword

    if (!currentPass) {
      errors.currentPassword = 'Current password is required.'
    }

    if (!newPass) {
      errors.newPassword = 'New password is required.'
    } else if (newPass.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters long.'
    } else if (!/[A-Z]/.test(newPass)) {
      errors.newPassword = 'Password must contain at least one uppercase letter.'
    } else if (!/[a-z]/.test(newPass)) {
      errors.newPassword = 'Password must contain at least one lowercase letter.'
    } else if (!/[0-9]/.test(newPass)) {
      errors.newPassword = 'Password must contain at least one number.'
    } else if (currentPass && currentPass === newPass) {
      errors.newPassword = 'New password must be different from current password.'
    }

    if (!confirmPass) {
      errors.confirmPassword = 'Please confirm your new password.'
    } else if (newPass && confirmPass !== newPass) {
      errors.confirmPassword = 'Passwords do not match.'
    }

    setPasswordErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validatePasswordForm()) {
      toast.error('Please fix the password validation errors.')
      return
    }

    try {
      setChangePasswordState('loading')
      await api.changeAdminPassword({
        currentPassword: passwordData.currentPassword.trim(),
        oldPassword: passwordData.currentPassword.trim(),
        newPassword: passwordData.newPassword,
        confirmPassword: passwordData.confirmPassword,
      })

      setChangePasswordState('success')
      toast.success('Password changed successfully. Please log in again.')
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })
      setPasswordErrors({})

      setTimeout(() => {
        logout()
        navigate('/login')
      }, 1500)
    } catch (error) {
      setChangePasswordState('error')
      handleApiError(error, 'Failed to change password')
      setTimeout(() => setChangePasswordState('idle'), 2000)
    }
  }

  if (profileLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6D3A0D]"></div>
      </div>
    )
  }

  return (
    <div className="space-y-8 max-w-4xl mx-auto pb-12">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-[#6D3A0D]">Admin Profile & Security</h1>
        <p className="text-sm text-gray-600">Manage your administrator details and security credentials</p>
      </div>

      {/* Profile Overview Card */}
      <div className="bg-white rounded-2xl border-2 border-[#F6EEE7] p-6 shadow-xs flex flex-col sm:flex-row items-center gap-6">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#6D3A0D] to-[#8B4513] text-white flex items-center justify-center font-bold text-2xl shadow-md border-4 border-white flex-shrink-0">
          {profileData.firstName ? profileData.firstName.charAt(0).toUpperCase() : 'A'}
        </div>
        <div className="flex-1 text-center sm:text-left space-y-1">
          <h2 className="text-xl font-bold text-gray-900">
            {profileData.firstName || profileData.lastName
              ? `${profileData.firstName} ${profileData.lastName}`.trim()
              : 'Administrator'}
          </h2>
          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-4 text-xs text-gray-600 pt-1">
            <span className="flex items-center gap-1.5 font-medium">
              <Mail size={14} className="text-[#6D3A0D]" /> {profileData.email}
            </span>
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-900 font-bold uppercase tracking-wider text-[10px] border border-amber-200">
              <ShieldCheck size={12} /> {profileData.role}
            </span>
          </div>
        </div>
      </div>

      {/* 2 Column Form Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Personal Details Form */}
        <div className="bg-white rounded-2xl border-2 border-[#F6EEE7] p-6 space-y-6 shadow-xs">
          <div className="flex items-center gap-3 border-b border-[#F6EEE7] pb-4">
            <div className="p-2 bg-[#F6EEE7] text-[#6D3A0D] rounded-xl">
              <User size={20} />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Personal Information</h3>
              <p className="text-xs text-gray-500">Update your name and contact info</p>
            </div>
          </div>

          <form onSubmit={handleProfileSave} className="space-y-4" noValidate>
            <div>
              <label className="block text-xs font-semibold text-[#6D3A0D] mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={profileData.firstName}
                onChange={(e) => {
                  setProfileData({ ...profileData, firstName: e.target.value })
                  if (profileErrors.firstName) setProfileErrors({ ...profileErrors, firstName: undefined })
                }}
                className={`w-full px-4 py-2.5 text-sm border-2 rounded-xl focus:outline-none bg-white ${
                  profileErrors.firstName ? 'border-red-500 focus:ring-2 focus:ring-red-300' : 'border-[#F6EEE7] focus:ring-2 focus:ring-[#6D3A0D]'
                }`}
                placeholder="Enter first name"
              />
              {profileErrors.firstName && (
                <p className="text-xs text-red-600 mt-1 font-medium">{profileErrors.firstName}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#6D3A0D] mb-1">Last Name</label>
              <input
                type="text"
                value={profileData.lastName}
                onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                className="w-full px-4 py-2.5 text-sm border-2 border-[#F6EEE7] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#6D3A0D] bg-white"
                placeholder="Enter last name"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#6D3A0D] mb-1">Email Address</label>
              <input
                type="email"
                value={profileData.email}
                disabled
                className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-500 cursor-not-allowed font-medium"
              />
              <p className="text-[11px] text-gray-400 mt-1">Admin email address cannot be changed directly.</p>
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#6D3A0D] mb-1">Phone Number</label>
              <input
                type="tel"
                value={profileData.phone}
                onChange={(e) => {
                  setProfileData({ ...profileData, phone: e.target.value })
                  if (profileErrors.phone) setProfileErrors({ ...profileErrors, phone: undefined })
                }}
                className={`w-full px-4 py-2.5 text-sm border-2 rounded-xl focus:outline-none bg-white ${
                  profileErrors.phone ? 'border-red-500 focus:ring-2 focus:ring-red-300' : 'border-[#F6EEE7] focus:ring-2 focus:ring-[#6D3A0D]'
                }`}
                placeholder="Enter 10-digit phone number"
              />
              {profileErrors.phone && (
                <p className="text-xs text-red-600 mt-1 font-medium">{profileErrors.phone}</p>
              )}
            </div>

            <div className="pt-2">
              <LoadingButton
                type="submit"
                state={saveProfileState}
                variant="primary"
                size="md"
                className="w-full py-2.5 bg-[#6D3A0D] hover:bg-[#5D2A0D] text-white font-bold rounded-xl text-sm flex items-center justify-center gap-2 shadow-xs cursor-pointer"
              >
                <Save size={16} /> Save Profile Changes
              </LoadingButton>
            </div>
          </form>
        </div>

        {/* Change Password Form */}
        <div className="bg-white rounded-2xl border-2 border-[#F6EEE7] p-6 space-y-6 shadow-xs">
          <div className="flex items-center gap-3 border-b border-[#F6EEE7] pb-4">
            <div className="p-2 bg-amber-50 text-amber-700 rounded-xl">
              <Lock size={20} />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Change Password</h3>
              <p className="text-xs text-gray-500">Update your admin login password securely</p>
            </div>
          </div>

          <form onSubmit={handleChangePassword} className="space-y-4" noValidate>
            <div>
              <label className="block text-xs font-semibold text-[#6D3A0D] mb-1">
                Current Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordData.currentPassword}
                  onChange={(e) => {
                    setPasswordData({ ...passwordData, currentPassword: e.target.value })
                    if (passwordErrors.currentPassword) setPasswordErrors({ ...passwordErrors, currentPassword: undefined })
                  }}
                  className={`w-full px-4 py-2.5 text-sm border-2 rounded-xl focus:outline-none bg-white pr-10 ${
                    passwordErrors.currentPassword ? 'border-red-500 focus:ring-2 focus:ring-red-300' : 'border-[#F6EEE7] focus:ring-2 focus:ring-[#6D3A0D]'
                  }`}
                  placeholder="Enter current password"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                >
                  {showCurrentPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {passwordErrors.currentPassword && (
                <p className="text-xs text-red-600 mt-1 font-medium">{passwordErrors.currentPassword}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#6D3A0D] mb-1">
                New Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordData.newPassword}
                  onChange={(e) => {
                    setPasswordData({ ...passwordData, newPassword: e.target.value })
                    if (passwordErrors.newPassword) setPasswordErrors({ ...passwordErrors, newPassword: undefined })
                  }}
                  className={`w-full px-4 py-2.5 text-sm border-2 rounded-xl focus:outline-none bg-white pr-10 ${
                    passwordErrors.newPassword ? 'border-red-500 focus:ring-2 focus:ring-red-300' : 'border-[#F6EEE7] focus:ring-2 focus:ring-[#6D3A0D]'
                  }`}
                  placeholder="Min 8 chars (A-Z, a-z, 0-9)"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                >
                  {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {passwordErrors.newPassword && (
                <p className="text-xs text-red-600 mt-1 font-medium">{passwordErrors.newPassword}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#6D3A0D] mb-1">
                Confirm New Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={passwordData.confirmPassword}
                  onChange={(e) => {
                    setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                    if (passwordErrors.confirmPassword) setPasswordErrors({ ...passwordErrors, confirmPassword: undefined })
                  }}
                  className={`w-full px-4 py-2.5 text-sm border-2 rounded-xl focus:outline-none bg-white pr-10 ${
                    passwordErrors.confirmPassword ? 'border-red-500 focus:ring-2 focus:ring-red-300' : 'border-[#F6EEE7] focus:ring-2 focus:ring-[#6D3A0D]'
                  }`}
                  placeholder="Re-enter new password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {passwordErrors.confirmPassword && (
                <p className="text-xs text-red-600 mt-1 font-medium">{passwordErrors.confirmPassword}</p>
              )}
            </div>

            <div className="pt-2">
              <LoadingButton
                type="submit"
                state={changePasswordState}
                variant="primary"
                size="md"
                className="w-full py-2.5 bg-[#6D3A0D] hover:bg-[#5D2A0D] text-white font-bold rounded-xl text-sm flex items-center justify-center gap-2 shadow-xs cursor-pointer"
              >
                <Lock size={16} /> Update Password
              </LoadingButton>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
